import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiClientService } from './api-client.service';
import { CompanyConfigService } from './company-config.service';
import { TokenService } from './token.service';
import { SearchHistory } from '@shared/interfaces/search.interfaces';
import { environment } from '@environments/environment';

interface SearchRecord {
  query: string;
  metadata: any;
  totalCount: number;
  id?: number;
  searchTerm: string;
  activeTab: string;
  items: any;
  timestamp: Date;
}

interface CompanySearchData {
  company: string;
  data: SearchRecord[];
}

@Injectable({
  providedIn: 'root'
})
export class SearchHistoryService {
  private dbName = 'search_history_db';
  private readonly DB_VERSION_KEY = 'search_history_db_version';
  // Read persisted version so ensureStoreExists increments survive page reloads.
  // Floor is 4 because some browsers already reached version 4 via a prior ensureStoreExists call.
  private dbVersion: number = Math.max(
    4,
    parseInt(localStorage.getItem('search_history_db_version') || '4', 10)
  );
  private db: IDBDatabase | null = null;
  private initializationPromise: Promise<void> | null = null;

  // Track created stores dynamically
  private createdStores: Set<string> = new Set();

  // Batch insert queue
  private searchQueue: SearchRecord[] = [];
  private saveQueueTimeout: any = null;
  private readonly BATCH_DELAY = 1000; // 1 second

  // Cache for search history
  private companySearchHistoryCache = new BehaviorSubject<any>(null);

  private apiClient = inject(ApiClientService);
  private companyConfigService = inject(CompanyConfigService);
  private tokenService = inject(TokenService);

  // Track current session start time
  private currentSessionStartTime: Date | null = null;
  private readonly SESSION_START_KEY = 'search_session_start_time';

  // Observable for cache
  companySearchHistory$ = this.companySearchHistoryCache.asObservable();

  constructor() {
    this.initializeDatabase();
    this.initializeCleanupBasedOnEnvironment();
    this.initializeSession();
  }

  /**
   * Initialize search session tracking
   * Called on service initialization (which happens on app load)
   */
  private initializeSession(): void {
    const storedSessionStart = localStorage.getItem(this.SESSION_START_KEY);

    if (storedSessionStart) {
      this.currentSessionStartTime = new Date(storedSessionStart);
      console.log('Search session restored. Session start time:', this.currentSessionStartTime);
    } else {
      // No session start time found, create new session
      this.startNewSession();
    }
  }

  /**
   * Start a new search session
   * Should be called when user logs in
   */
  startNewSession(): void {
    this.currentSessionStartTime = new Date();
    localStorage.setItem(this.SESSION_START_KEY, this.currentSessionStartTime.toISOString());
    console.log('New search session started at:', this.currentSessionStartTime.toISOString());
  }

  /**
   * Clear session tracking
   * Should be called when user logs out
   */
  clearSession(): void {
    this.currentSessionStartTime = null;
    localStorage.removeItem(this.SESSION_START_KEY);
    console.log('Search session cleared');
  }

  /**
   * Initialize companies from authentication system
   * Should be called after successful login to ensure IndexedDB stores exist for all companies
   */
  async initializeCompaniesFromAuth(): Promise<void> {
    console.log('ðŸ”§ Initializing search history for all authenticated companies...');

    try {
      // Get all companies from CompanyConfigService
      const companies = this.companyConfigService.getAvailableCompanies();

      if (companies.length === 0) {
        console.warn('âš ï¸ No companies found in CompanyConfigService');
        return;
      }

      console.log(`âœ“ Found ${companies.length} companies:`, companies);

      // Ensure IndexedDB stores exist for all companies
      for (const company of companies) {
        console.log(`  â†’ Ensuring store exists for: ${company}`);
        await this.ensureStoreExists(company);
      }

      console.log('âœ“ Search history initialization complete for all companies');
    } catch (error) {
      console.error('âœ— Error initializing companies from auth:', error);
    }
  }

  /**
   * Initialize the IndexedDB database.
   * Probes the real DB version first so a stale/cleared localStorage never
   * causes a VersionError ("requested N < existing M").
   */
  private initializeDatabase(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = new Promise<void>((resolve, reject) => {
      // Open without a version to read the real current version from the browser.
      // This never triggers onupgradeneeded; it just tells us what version the DB is at.
      const probe = indexedDB.open(this.dbName);

      probe.onsuccess = () => {
        const realVersion = probe.result.version;
        probe.result.close();

        // If the DB is already ahead of our tracked version (e.g. localStorage was
        // cleared), sync up so we never request a lower version than what exists.
        if (realVersion > this.dbVersion) {
          this.dbVersion = realVersion;
          localStorage.setItem(this.DB_VERSION_KEY, String(this.dbVersion));
        }

        this.openDB(resolve, reject);
      };

      // If the probe itself fails, fall through and let openDB surface the error.
      probe.onerror = () => this.openDB(resolve, reject);
    });

    return this.initializationPromise;
  }

  private openDB(resolve: () => void, reject: (e: any) => void): void {
    const request = indexedDB.open(this.dbName, this.dbVersion);

    request.onerror = () => {
      console.error('Error opening search history database:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      this.db = request.result;

      // Track existing stores
      for (let i = 0; i < this.db.objectStoreNames.length; i++) {
        this.createdStores.add(this.db.objectStoreNames[i]);
      }

      console.log('Search history database opened successfully');
      console.log('Existing stores:', Array.from(this.createdStores));
      resolve();
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Get list of known companies from localStorage or use defaults
      const knownCompanies = this.getKnownCompanies();

      console.log('Creating stores for companies:', knownCompanies);

      // Create stores for all known companies
      knownCompanies.forEach(company => {
        const storeName = this.getStoreNameForCompany(company);

        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, {
            keyPath: 'id',
            autoIncrement: true
          });
          store.createIndex('searchTerm', 'searchTerm', { unique: false });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('activeTab', 'activeTab', { unique: false });

          this.createdStores.add(storeName);
          console.log(`Created store: ${storeName}`);
        }
      });

      console.log('Search history database stores created/updated');
    };
  }


  /**
   * Get list of known companies dynamically from localStorage
   * Scans for all company user-details keys with pattern: tibss.{companyName}-userDetails
   */
  private getKnownCompanies(): string[] {
    console.log('ðŸ” Getting known companies dynamically...');

    // Scan localStorage for company user-details keys
    const companies = this.getCompaniesFromLocalStorage();

    console.log(`âœ“ Found ${companies.length} companies from localStorage:`, companies);

    // If no companies found in localStorage, return empty array
    // This happens on initial load before login
    if (companies.length === 0) {
      console.warn('âš ï¸ No companies found in localStorage (user may not be logged in)');
      return [];
    }

    return companies;
  }

  /**
   * Scan localStorage for all companies by looking for user-details keys
   * Pattern: tibss.{companyName}-userDetails
   */
  private getCompaniesFromLocalStorage(): string[] {
    const companies: string[] = [];
    const keys = Object.keys(localStorage);

    keys.forEach(key => {
      // Pattern: tibss.{companyName}-userDetails
      if (key.startsWith('tibss.') && key.endsWith('-userDetails')) {
        const company = key.replace('tibss.', '').replace('-userDetails', '');
        if (!companies.includes(company)) {
          companies.push(company);
          console.log(`  â†’ Found company: ${company}`);
        }
      }
    });

    return companies;
  }

  /**
   * Add a new company to known companies
   * Note: This is now a no-op since companies are auto-discovered from localStorage
   * Kept for backward compatibility
   * @deprecated Companies are now auto-discovered from localStorage userDetails
   */
  private addKnownCompany(company: string): void {
    // No longer needed - companies are dynamically discovered from localStorage
    console.log(`ðŸ“ Company registration requested for: ${company} (auto-discovered, no action needed)`);
  }

  /**
   * Get store name for a company
   */
  private getStoreNameForCompany(company: string): string {
    return `${company.toLowerCase()}_searches`;
  }

  /**
   * Ensure store exists for current company
   */
  private async ensureStoreExists(company: string): Promise<void> {
    const storeName = this.getStoreNameForCompany(company);

    if (this.createdStores.has(storeName)) {
      return; // Store already exists
    }

    // Add to known companies
    this.addKnownCompany(company);

    // Need to upgrade database to add new store
    if (this.db) {
      this.db.close();
      this.db = null;
      this.initializationPromise = null;
      this.dbVersion++;
      localStorage.setItem(this.DB_VERSION_KEY, String(this.dbVersion));
    }

    await this.initializeDatabase();
  }
  
  /**
   * Get current company name
   */
  private getCurrentCompany(): string {
    return localStorage.getItem('currentCompany') || 'unity';
  }

  /**
   * Get store name for current company
   */
  private getStoreName(): string {
    const company = this.getCurrentCompany();
    return this.getStoreNameForCompany(company);
  }
  
  /**
   * Ensure database is initialized
   */
  private async ensureDBInitialized(): Promise<void> {
    if (!this.db) {
      await this.initializeDatabase();
    }
  }
  
  /**
   * Save search record with batch processing
   */
  async saveSearchRecord(
    searchTerm: string,
    activeTab: string,
    items: any
  ): Promise<void> {
    try {
      const record: SearchRecord = {
        searchTerm,
        activeTab,
        items,
        timestamp: new Date(),
        query: '',
        metadata: undefined,
        totalCount: 0
      };
      
      // Add to batch queue
      this.searchQueue.push(record);
      
      // Schedule batch write if not already scheduled
      if (!this.saveQueueTimeout) {
        this.saveQueueTimeout = setTimeout(() => {
          this.flushSearchQueue();
        }, this.BATCH_DELAY);
      }
      
      console.log('Search record queued for batch write:', { searchTerm, activeTab });
    } catch (error) {
      console.error('Error queuing search record:', error);
    }
  }
  
  /**
   * Save a search record immediately (no batch delay).
   * Use this on result click so the record is in IndexedDB before navigation.
   */
  async saveSearchRecordImmediate(
    searchTerm: string,
    activeTab: string,
    items: any
  ): Promise<void> {
    try {
      // Cancel any pending batch timer and flush existing queue first
      if (this.saveQueueTimeout) {
        clearTimeout(this.saveQueueTimeout);
        this.saveQueueTimeout = null;
        await this.flushSearchQueue();
      }

      await this.ensureDBInitialized();
      const company = this.getCurrentCompany();
      await this.ensureStoreExists(company);

      if (!this.db) throw new Error('Database not available');

      // Normalise: activeTab always lowercase, items always an array
      const normalisedTab = (activeTab || '').toLowerCase();
      const normalisedItems = Array.isArray(items) ? items : items ? [items] : [];

      const record: SearchRecord = {
        searchTerm,
        activeTab: normalisedTab,
        items: normalisedItems,
        timestamp: new Date(),
        query: '',
        metadata: undefined,
        totalCount: 0
      };

      const storeName = this.getStoreName();
      await new Promise<void>((resolve, reject) => {
        const tx = this.db!.transaction([storeName], 'readwrite');
        const req = tx.objectStore(storeName).add(record);
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });

      console.log('Search record saved immediately:', { searchTerm, normalisedTab });
    } catch (error) {
      console.error('Error saving search record immediately:', error);
    }
  }

  /**
   * Flush search queue and batch write to IndexedDB
   */
  private async flushSearchQueue(): Promise<void> {
    if (this.searchQueue.length === 0) {
      this.saveQueueTimeout = null;
      return;
    }

    try {
      await this.ensureDBInitialized();

      // Ensure store exists for current company
      const company = this.getCurrentCompany();
      await this.ensureStoreExists(company);

      if (!this.db) {
        throw new Error('Database not available');
      }

      const storeName = this.getStoreName();
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);

      const recordsToWrite = [...this.searchQueue];
      this.searchQueue = [];
      this.saveQueueTimeout = null;

      console.log(`Batch writing ${recordsToWrite.length} search records to ${storeName}`);

      // Write all records
      const promises = recordsToWrite.map(record => {
        return new Promise<void>((resolve, reject) => {
          const request = store.add(record);
          request.onsuccess = () => resolve();
          request.onerror = () => reject(request.error);
        });
      });

      await Promise.all(promises);
      console.log('Batch write completed successfully');

    } catch (error) {
      console.error('Error in batch write:', error);
      // Reset queue timeout for retry
      this.saveQueueTimeout = null;
    }
  }
  
  /**
   * Get all search records for current company
   */
  async getAllSearchRecords(): Promise<SearchRecord[]> {
    try {
      await this.ensureDBInitialized();
      
      if (!this.db) {
        throw new Error('Database not available');
      }
      
      const storeName = this.getStoreName();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([storeName], 'readonly');
        const store = transaction.objectStore(storeName);
        const request = store.getAll();
        
        request.onsuccess = () => {
          const records = (request.result as SearchRecord[])
            .filter(record => record.searchTerm || record.activeTab)
            .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
          
          console.log(`Retrieved ${records.length} search records from ${storeName}`);
          resolve(records);
        };
        
        request.onerror = () => {
          console.error(`Error fetching search records from ${storeName}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error getting search records:', error);
      return [];
    }
  }
  
  /**
   * Get recent search records
   */
  async getRecentSearchRecords(limit: number = 5): Promise<SearchRecord[]> {
    try {
      const allRecords = await this.getAllSearchRecords();
      return allRecords.slice(0, limit);
    } catch (error) {
      console.error('Error getting recent search records:', error);
      return [];
    }
  }
  
  /**
   * Get search records from current session only (since login)
   * This is used to send data to backend API
   */
  async getCurrentSessionRecords(): Promise<CompanySearchData[]> {
    console.log('    â†’ getCurrentSessionRecords() START');
    console.log(`    â†’ Session start time: ${this.currentSessionStartTime?.toISOString() || 'NOT SET'}`);

    if (!this.currentSessionStartTime) {
      console.warn('    âœ— No session start time set, cannot filter by session');
      return [];
    }

    try {
      // Get all records first
      const allCompanyRecords = await this.getAllCompanyRecords();

      // Filter records to include only those from current session
      const sessionRecords: CompanySearchData[] = allCompanyRecords.map(companyData => {
        const sessionFilteredData = companyData.data.filter(record => {
          const recordTime = new Date(record.timestamp);
          const isInSession = recordTime >= this.currentSessionStartTime!;

          if (!isInSession) {
            console.log(`      Excluding record from ${recordTime.toISOString()} (before session start)`);
          }

          return isInSession;
        });

        console.log(`    â†’ Company ${companyData.company}: ${sessionFilteredData.length}/${companyData.data.length} records in current session`);

        return {
          company: companyData.company,
          data: sessionFilteredData
        };
      });

      const totalSessionRecords = sessionRecords.reduce((sum, c) => sum + c.data.length, 0);
      console.log(`    â†’ getCurrentSessionRecords() COMPLETE - ${totalSessionRecords} total session records`);

      return sessionRecords;
    } catch (error) {
      console.error('    âœ—âœ—âœ— Error in getCurrentSessionRecords():', error);
      return [];
    }
  }

  /**
   * Get search records from all companies (all time, not filtered by session)
   */
  async getAllCompanyRecords(): Promise<CompanySearchData[]> {
    console.log('    â†’ getAllCompanyRecords() START');
    try {
      console.log('    â†’ Ensuring DB is initialized...');
      await this.ensureDBInitialized();
      console.log('    â†’ DB initialization complete');

      if (!this.db) {
        console.error('    âœ— Database not available after initialization!');
        throw new Error('Database not available');
      }

      console.log('    â†’ Database is available');
      console.log('    â†’ Database name:', this.db.name);
      console.log('    â†’ Database version:', this.db.version);

      // Get all company stores dynamically
      const knownCompanies = this.getKnownCompanies();
      console.log('    â†’ Known companies:', knownCompanies);

      const results: CompanySearchData[] = [];

      for (const company of knownCompanies) {
        console.log(`    â†’ Processing company: ${company}`);
        const storeName = this.getStoreNameForCompany(company);
        console.log(`      Store name: ${storeName}`);

        // Check if store exists
        const storeExists = this.db.objectStoreNames.contains(storeName);
        console.log(`      Store exists: ${storeExists}`);

        if (!storeExists) {
          console.warn(`      âœ— Store ${storeName} does not exist, skipping`);
          results.push({ company, data: [] });
          continue;
        }

        try {
          console.log(`      â†’ Opening transaction for ${storeName}...`);
          const records = await new Promise<SearchRecord[]>((resolve, reject) => {
            const transaction = this.db!.transaction([storeName], 'readonly');
            console.log(`        Transaction created`);

            const store = transaction.objectStore(storeName);
            console.log(`        Object store accessed`);

            const request = store.getAll();
            console.log(`        getAll() request initiated`);

            request.onsuccess = () => {
              console.log(`        âœ“ getAll() succeeded, records count: ${request.result.length}`);
              resolve(request.result as SearchRecord[]);
            };

            request.onerror = () => {
              console.error(`        âœ— getAll() failed:`, request.error);
              reject(request.error);
            };
          });

          console.log(`      â†’ Filtering records...`);
          const filteredRecords = records
            .filter(record => record.searchTerm || record.activeTab)
            .map(record => ({
              ...record,
              timestamp: this.convertTimestamp(record.timestamp)
            }));

          console.log(`      â†’ Filtered ${filteredRecords.length} valid records from ${records.length} total`);

          results.push({
            company,
            data: filteredRecords
          });

          if (filteredRecords.length > 0) {
            console.log(`      â†’ Sample record:`, filteredRecords[0]);
          }
        } catch (error) {
          console.error(`      âœ— Error fetching records from ${storeName}:`, error);
          results.push({ company, data: [] });
        }
      }

      console.log('    â†’ getAllCompanyRecords() COMPLETE');
      console.log('    â†’ Summary:');
      results.forEach(r => {
        console.log(`      - ${r.company}: ${r.data.length} records`);
      });

      return results;
    } catch (error) {
      console.error('    âœ—âœ—âœ— Error in getAllCompanyRecords():', error);
      console.error('    Stack:', (error as Error).stack);
      return [];
    }
  }
  
  /**
   * Convert timestamp to string format
   */
  private convertTimestamp(timestamp: any): Date {
    if (timestamp instanceof Date) {
      return timestamp;
    }
    
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      return new Date();
    }
    
    return date;
  }
  
  /**
   * Clear search history for current company
   */
  async clearSearchHistory(): Promise<void> {
    try {
      await this.ensureDBInitialized();
      
      if (!this.db) {
        throw new Error('Database not available');
      }
      
      const storeName = this.getStoreName();
      
      return new Promise((resolve, reject) => {
        const transaction = this.db!.transaction([storeName], 'readwrite');
        const store = transaction.objectStore(storeName);
        const request = store.clear();
        
        request.onsuccess = () => {
          console.log(`Search history cleared for ${storeName}`);
          resolve();
        };
        
        request.onerror = () => {
          console.error(`Error clearing search history for ${storeName}:`, request.error);
          reject(request.error);
        };
      });
    } catch (error) {
      console.error('Error clearing search history:', error);
      throw error;
    }
  }
  
  /**
   * Cleanup old search records
   */
  async cleanupOldRecords(daysToKeep: number = 30): Promise<void> {
    try {
      await this.ensureDBInitialized();
      
      if (!this.db) {
        throw new Error('Database not available');
      }
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysToKeep);
      
      const storeName = this.getStoreName();
      const transaction = this.db.transaction([storeName], 'readwrite');
      const store = transaction.objectStore(storeName);
      const index = store.index('timestamp');
      
      const range = IDBKeyRange.upperBound(cutoffDate);
      const request = index.openCursor(range);
      
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      
      console.log(`Cleaned up search records older than ${daysToKeep} days`);
    } catch (error) {
      console.error('Error cleaning up old records:', error);
    }
  }
  
  /**
   * Get company search history from backend
   */
  getCompanySearchHistory(userId: string, forceRefresh: boolean = false): Observable<any> {
    // Check cache first (skip cache if forceRefresh is true)
    if (!forceRefresh && this.companySearchHistoryCache.value) {
      return this.companySearchHistoryCache.asObservable();
    }

    const params = { agentId: userId, perPage: 50 };
    const request = this.apiClient.post('api/orders/getGlobalSearchLog', params);

    // Cache the response
    request.subscribe(
      (data) => {
        this.companySearchHistoryCache.next(data);
      },
      (error) => {
        console.error('Error fetching company search history:', error);
      }
    );

    return request;
  }

  /**
   * Clear the search history cache
   */
  clearSearchHistoryCache(): void {
    this.companySearchHistoryCache.next(null);
  }
  
  /**
   * Send search data to backend - FULLY DYNAMIC for any company
   */
  async sendSearchDataToBackend(searchData: CompanySearchData): Promise<any> {
    console.log(`â–¶ sendSearchDataToBackend() START for company: ${searchData.company}`);

    try {
      let baseUrl = '';
      let token = '';
      let userId: number | null = null;

      // âœ… DYNAMIC: Get company configuration from CompanyConfigService
      console.log(`  ðŸ” Getting configuration for company: ${searchData.company}`);
      const companyConfig = this.companyConfigService.getCompanyConfig(searchData.company);

      if (companyConfig) {
        // Use company config from CompanyConfigService (most reliable)
        console.log(`  âœ“ Found company config from CompanyConfigService`);
        baseUrl = companyConfig.baseUrl;
        token = this.tokenService.getToken(searchData.company) || companyConfig.token || '';
        userId = companyConfig.user?.userID || null;

        console.log(`  Base URL from config: ${baseUrl}`);
        console.log(`  Token from config: ${token ? 'EXISTS' : 'MISSING'}`);
        console.log(`  User ID from config: ${userId}`);
      } else {
        // Fallback: Try memory token + non-sensitive local user details
        console.log(`  âš ï¸ No company config found, trying memory/local userDetails...`);

        const userDetailsKey = `tibss.${searchData.company}-userDetails`;
        token = this.tokenService.getToken(searchData.company) || this.tokenService.getCurrentToken() || '';
        console.log(`  Token from memory: ${token ? `EXISTS (length: ${token.length})` : 'NOT FOUND'}`);

        console.log(`  Looking for user details at: ${userDetailsKey}`);
        const userDetailsStr = localStorage.getItem(userDetailsKey);
        if (userDetailsStr) {
          try {
            const userDetails = JSON.parse(userDetailsStr);
            userId = userDetails?.userID;
            console.log(`  User ID from localStorage: ${userId}`);
          } catch (e) {
            console.error(`  Failed to parse user details:`, e);
          }
        } else {
          console.log(`  User details NOT FOUND in localStorage`);
        }

        // Get base URL from localStorage
        const currentCompany = localStorage.getItem('currentCompany');
        if (currentCompany === searchData.company) {
          baseUrl = localStorage.getItem('BaseUrl') || '';
          console.log(`  Base URL from localStorage: ${baseUrl}`);
        } else {
          console.warn(`  âš ï¸ Cannot get base URL - not the current company`);
        }
      }

      // Remove quotes if present
      token = token.replace(/^\"(.*)\"$/, '$1');

      if (!token) {
        console.error(`  âœ— CRITICAL: No token available for company: ${searchData.company}`);
        throw new Error(`Authentication token missing for ${searchData.company}`);
      }

      const apiUrl = `${baseUrl}api/orders/globalSearchLog`;
      console.log(`  Final API URL: ${apiUrl}`);
      console.log(`  User ID: ${userId}`);
      console.log(`  Token: ${token.substring(0, 20)}...`);
      console.log(`  Payload record count: ${searchData.data.length}`);

      console.log(`  Making HTTP POST request...`);
      console.log(`  Request details:`, {
        url: apiUrl,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token.substring(0, 20)}...`,
          'Content-Type': 'application/json'
        },
        bodyRecordCount: searchData.data.length,
        showLoading: false
      });

      // Log the actual payload being sent (first record as sample)
      if (searchData.data.length > 0) {
        console.log(`  Sample payload record:`, {
          company: searchData.company,
          firstRecord: searchData.data[0],
          totalRecords: searchData.data.length
        });
      }

      console.log(`  Calling this.apiClient.post()...`);

      let observable;
      try {
        observable = this.apiClient.post(apiUrl, searchData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          showLoading: false // Don't show loading during logout
        });
        console.log(`  âœ“ Observable created successfully`);
        console.log(`  Observable type:`, typeof observable);
        console.log(`  Observable has toPromise:`, typeof observable.toPromise === 'function');
      } catch (obsError) {
        console.error(`  âœ— Failed to create observable:`, obsError);
        throw obsError;
      }

      console.log(`  Converting to Promise with toPromise()...`);
      let promise;
      try {
        promise = observable.toPromise();
        console.log(`  âœ“ Promise created successfully`);
        console.log(`  Promise type:`, typeof promise);
      } catch (promiseError) {
        console.error(`  âœ— Failed to convert to promise:`, promiseError);
        throw promiseError;
      }

      console.log(`  Awaiting promise resolution...`);
      let result;
      try {
        result = await promise;
        console.log(`  âœ“ Promise resolved successfully`);
      } catch (awaitError) {
        console.error(`  âœ— Promise rejected with error:`, awaitError);
        console.error(`  Error type:`, typeof awaitError);
        console.error(`  Error name:`, (awaitError as any)?.name);
        console.error(`  Error message:`, (awaitError as any)?.message);
        console.error(`  Error status:`, (awaitError as any)?.status);
        console.error(`  Full error object:`, JSON.stringify(awaitError, null, 2));
        throw awaitError;
      }

      console.log(`  âœ“ API call successful for ${searchData.company}`);
      console.log(`  Response:`, result);
      console.log(`â–¶ sendSearchDataToBackend() END for company: ${searchData.company}`);

      return result;

    } catch (error) {
      console.error(`  âœ—âœ—âœ— EXCEPTION in sendSearchDataToBackend for ${searchData.company} âœ—âœ—âœ—`);
      console.error(`  Error:`, error);
      console.error(`  Error type:`, typeof error);
      console.error(`  Error constructor:`, (error as any)?.constructor?.name);
      console.error(`  Error message:`, (error as any)?.message);
      console.error(`  Error stack:`, (error as any)?.stack);
      console.error(`â–¶ sendSearchDataToBackend() FAILED for company: ${searchData.company}`);
      throw error;
    }
  }

  /**
   * Send all search data to backend before logout
   * This method retrieves all company search records and sends them to the backend
   */
  async sendAllSearchDataOnLogout(): Promise<void> {
    const startTime = performance.now();
    console.log('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');
    console.log('START: sendAllSearchDataOnLogout()');
    console.log(`Timestamp: ${new Date().toISOString()}`);
    console.log(`Performance timer start: ${startTime.toFixed(2)}ms`);
    console.log('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');

    try {
      // Step 1: Check localStorage state BEFORE doing anything
      const step1Start = performance.now();
      console.log(`STEP 1: Checking localStorage state... [${(step1Start - startTime).toFixed(2)}ms]`);
      const currentCompany = localStorage.getItem('currentCompany') || 'unity';
      const currentToken = this.tokenService.getToken(currentCompany);
      const currentUserDetails = localStorage.getItem(`tibss.${currentCompany}-userDetails`);

      console.log('localStorage State:', {
        currentCompany,
        currentToken: currentToken ? `EXISTS (length: ${currentToken.length})` : 'NOT FOUND',
        currentUserDetails: currentUserDetails ? 'EXISTS' : 'NOT FOUND'
      });
      console.log(`Step 1 completed in ${(performance.now() - step1Start).toFixed(2)}ms`);

      // Step 2: Flush any pending searches in the queue
      const step2Start = performance.now();
      console.log(`STEP 2: Flushing search queue... [${(step2Start - startTime).toFixed(2)}ms]`);
      console.log(`Queue has ${this.searchQueue.length} pending items`);
      await this.flushSearchQueue();
      console.log(`Search queue flushed successfully in ${(performance.now() - step2Start).toFixed(2)}ms`);

      // Step 3: Get current session records from IndexedDB (only records since login)
      const step3Start = performance.now();
      console.log(`STEP 3: Retrieving CURRENT SESSION records from IndexedDB... [${(step3Start - startTime).toFixed(2)}ms]`);
      console.log(`  Session start time: ${this.currentSessionStartTime?.toISOString() || 'NOT SET'}`);
      const allCompanyRecords = await this.getCurrentSessionRecords();
      console.log(`Retrieved ${allCompanyRecords.length} company record sets (session only) in ${(performance.now() - step3Start).toFixed(2)}ms`);

      // Log detailed info about each company's data
      allCompanyRecords.forEach(companyData => {
        console.log(`Company: ${companyData.company}, Records: ${companyData.data.length}`);
        if (companyData.data.length > 0) {
          console.log(`  Sample record:`, companyData.data[0]);
        }
      });

      if (!allCompanyRecords || allCompanyRecords.length === 0) {
        console.log('RESULT: No search data to send to backend');
        console.log(`Total time elapsed: ${(performance.now() - startTime).toFixed(2)}ms`);
        console.log('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');
        console.log('END: sendAllSearchDataOnLogout() - NO DATA');
        console.log('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');
        return;
      }

      // Step 4: Filter companies with data
      const step4Start = performance.now();
      console.log(`STEP 4: Filtering companies... [${(step4Start - startTime).toFixed(2)}ms]`);
      const companiesWithData = allCompanyRecords.filter(
        companyData => companyData.data && companyData.data.length > 0
      );

      console.log(`Found ${companiesWithData.length} companies with data to send (took ${(performance.now() - step4Start).toFixed(2)}ms)`);

      // Step 5: Send each company's data to backend
      const step5Start = performance.now();
      console.log(`STEP 5: Sending data to backend for each company... [${(step5Start - startTime).toFixed(2)}ms]`);

      const sendPromises = companiesWithData.map(async (companyData, index) => {
        const companyStart = performance.now();
        console.log(`â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€`);
        console.log(`Sending data for company ${index + 1}/${companiesWithData.length}: ${companyData.company}`);
        console.log(`Records to send: ${companyData.data.length}`);
        console.log(`Company timer start: ${companyStart.toFixed(2)}ms`);

        try {
          const result = await this.sendSearchDataToBackend(companyData);
          const companyDuration = performance.now() - companyStart;
          console.log(`âœ“ Successfully sent data for ${companyData.company} in ${companyDuration.toFixed(2)}ms`);
          console.log(`  API Response:`, result);
          return result;
        } catch (error) {
          const companyDuration = performance.now() - companyStart;
          console.error(`âœ— Failed to send data for ${companyData.company} after ${companyDuration.toFixed(2)}ms:`);
          console.error(`  Error:`, error);
          // Don't throw error, just log it so other companies can still be processed
          return null;
        }
      });

      // Step 6: Wait for all requests to complete
      const step6Start = performance.now();
      console.log(`STEP 6: Waiting for all API calls to complete... [${(step6Start - startTime).toFixed(2)}ms]`);
      const results = await Promise.allSettled(sendPromises);
      console.log(`All API calls completed in ${(performance.now() - step6Start).toFixed(2)}ms`);

      // Log results summary
      console.log('API Calls Summary:');
      results.forEach((result, index) => {
        const company = companiesWithData[index].company;
        if (result.status === 'fulfilled') {
          console.log(`  âœ“ ${company}: SUCCESS`);
        } else {
          console.log(`  âœ— ${company}: FAILED -`, result.reason);
        }
      });

      const totalTime = performance.now() - startTime;
      console.log('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');
      console.log('END: sendAllSearchDataOnLogout() - SUCCESS');
      console.log(`Total execution time: ${totalTime.toFixed(2)}ms (${(totalTime / 1000).toFixed(2)}s)`);
      console.log(`Timestamp: ${new Date().toISOString()}`);
      console.log('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');

      // Step 7: Clear search history based on environment configuration
      console.log('\nSTEP 7: Clearing search history based on environment configuration...');
      try {
        await this.clearSearchHistoryOnLogout();
        console.log('âœ“ Search history cleanup completed');
      } catch (clearError) {
        console.error('âœ— Failed to clear search history:', clearError);
        // Don't throw error, just log it
      }

      // Step 8: Clear session tracking (always do this on logout)
      console.log('\nSTEP 8: Clearing session tracking...');
      this.clearSession();
      console.log('âœ“ Session tracking cleared');

    } catch (error) {
      const totalTime = performance.now() - startTime;
      console.error('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');
      console.error('ERROR in sendAllSearchDataOnLogout():');
      console.error('Error:', error);
      console.error('Stack:', (error as Error).stack);
      console.error(`Failed after ${totalTime.toFixed(2)}ms`);
      console.error('â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•â•');
      // Don't throw error to prevent logout from failing
    }
  }
  
  /**
   * Initialize search history cleanup based on environment settings
   */
  private initializeCleanupBasedOnEnvironment(): void {
    const storageBasis = environment.searchHistoryStorageBasis || 'login';
    const daysToKeep = environment.searchHistoryDaysToKeep || 30;

    console.log('Search History Configuration:', {
      storageBasis,
      daysToKeep: storageBasis === 'days' ? daysToKeep : 'N/A (login-based)'
    });

    if (storageBasis === 'days') {
      // Day-based storage: cleanup old records on initialization
      console.log(`Initializing day-based search history (keep for ${daysToKeep} days)`);
      this.cleanupOldRecords(daysToKeep);

      // Optional: Set up periodic cleanup (every 24 hours)
      setInterval(() => {
        console.log(`Running periodic cleanup (removing records older than ${daysToKeep} days)`);
        this.cleanupOldRecords(daysToKeep);
      }, 24 * 60 * 60 * 1000); // 24 hours
    } else {
      // Login-based storage: records are kept until logout
      console.log('Initializing login-based search history (cleared on logout only)');
      // Records will be cleared during logout via sendAllSearchDataOnLogout
      // No automatic cleanup needed
    }
  }

  /**
   * Clear search history based on environment configuration
   * Called during logout for login-based storage
   */
  async clearSearchHistoryOnLogout(): Promise<void> {
    const storageBasis = environment.searchHistoryStorageBasis || 'login';

    console.log(`Clearing search history on logout (mode: ${storageBasis})`);

    if (storageBasis === 'login') {
      // Login-based: Clear all search history
      console.log('Login-based mode: Clearing ALL search history for all companies');

      const knownCompanies = this.getKnownCompanies();
      for (const company of knownCompanies) {
        try {
          await this.ensureDBInitialized();

          if (!this.db) {
            console.warn('Database not available for clearing');
            continue;
          }

          const storeName = this.getStoreNameForCompany(company);

          if (!this.db.objectStoreNames.contains(storeName)) {
            console.warn(`Store ${storeName} does not exist`);
            continue;
          }

          await new Promise<void>((resolve, reject) => {
            const transaction = this.db!.transaction([storeName], 'readwrite');
            const store = transaction.objectStore(storeName);
            const request = store.clear();

            request.onsuccess = () => {
              console.log(`âœ“ Cleared all search history for ${company}`);
              resolve();
            };

            request.onerror = () => {
              console.error(`âœ— Failed to clear search history for ${company}:`, request.error);
              reject(request.error);
            };
          });
        } catch (error) {
          console.error(`Error clearing search history for ${company}:`, error);
        }
      }
    } else {
      // Day-based: Don't clear on logout, records persist based on age
      console.log('Day-based mode: Search history NOT cleared on logout (managed by age)');
    }
  }

  /**
   * @deprecated Use initializeCleanupBasedOnEnvironment instead
   */
  initializeCleanup(): void {
    this.initializeCleanupBasedOnEnvironment();
  }
}

